import React from 'react';
import { Modal, Badge, Table, Accordion } from 'react-bootstrap';
import ButtonGeneral from './buttons/ButtonGeneral';

interface BrandDetail {
    TipoCategoria?: string;
    TipoCategoriaDescripcion?: string;
    TipoMarca?: string;
    TipoMarcaDescripcion?: string;
    Estado?: string;
    EstadoDescripcion?: string;
    NumeroSolicitud?: string;
    NumeroRegistro?: number;
    FechaPresentacion?: string;
    FechaPublicacion?: string;
    FechaRegistro?: string;
    FechaVencimiento?: string;
    Denominacion?: string;
    Traduccion?: string;
    EtiquetaDescripcion?: string;
    Proteccion?: string;
    Titulares?: Array<{
        Nombre?: string;
        Domicilio?: string;
        IdPais?: string;
        NomPais?: string;
    }>;
    Clases?: Array<{
        Id?: number;
        Cobertura?: string;
        Instancia?: string;
    }>;
    Instancias?: Array<{
        Fecha?: string;
        EstadoDescripcion?: string;
        Observacion?: string;
    }>;
}

interface Props {
    show: boolean;
    onHide: () => void;
    brandDetail: BrandDetail | null;
    loading: boolean;
}

const BrandDetailModal: React.FC<Props> = ({ show, onHide, brandDetail, loading }) => {
    if (loading) {
        return (
            <Modal show={show} onHide={onHide} size="xl" centered>
                <Modal.Body className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                    <p className="mt-3 text-muted">Consultando INAPI...</p>
                </Modal.Body>
            </Modal>
        );
    }
    
    if (!brandDetail) return null;
    
    const getEstadoColor = (estado?: string) => {
        if (!estado) return 'secondary';
        if (estado.includes('CONC') || estado.includes('Registrada')) return 'success';
        if (estado.includes('PEND') || estado.includes('Pendiente')) return 'warning';
        if (estado.includes('RECH') || estado.includes('Rechazada')) return 'danger';
        return 'info';
    };
    
    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="fa fa-file-alt me-2"></i>
                    Detalle de Marca - INAPI
                </Modal.Title>
            </Modal.Header>
            
            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {/* Header con denominación y estado */}
                <div className="text-center mb-4 pb-3 border-bottom">
                    <h4 className="mb-3">{brandDetail.Denominacion || 'N/A'}</h4>
                    <div className="d-flex justify-content-center align-items-center gap-3 flex-wrap">
                        <Badge bg={getEstadoColor(brandDetail.Estado)} className="p-2">
                            <i className="fa fa-info-circle me-2"></i>
                            {brandDetail.EstadoDescripcion || 'N/A'}
                        </Badge>
                        <Badge bg="primary" className="p-2">
                            Solicitud: {brandDetail.NumeroSolicitud || 'N/A'}
                        </Badge>
                        {brandDetail.NumeroRegistro && (
                            <Badge bg="secondary" className="p-2">
                                Registro: {brandDetail.NumeroRegistro}
                            </Badge>
                        )}
                    </div>
                </div>

                {/* Información General */}
                <div className="card mb-3">
                    <div className="card-header bg-light">
                        <h6 className="mb-0">
                            <i className="fa fa-info-circle me-2 text-primary"></i>
                            Información General
                        </h6>
                    </div>
                    <div className="card-body">
                        <div className="row g-3">
                            <div className="col-md-6">
                                <strong>Tipo de Marca:</strong>
                                <p className="mb-0">{brandDetail.TipoMarcaDescripcion || 'N/A'}</p>
                            </div>
                            <div className="col-md-6">
                                <strong>Categoría:</strong>
                                <p className="mb-0">{brandDetail.TipoCategoriaDescripcion || 'N/A'}</p>
                            </div>
                            <div className="col-md-6">
                                <strong>Fecha Presentación:</strong>
                                <p className="mb-0">{brandDetail.FechaPresentacion || 'N/A'}</p>
                            </div>
                            <div className="col-md-6">
                                <strong>Fecha Registro:</strong>
                                <p className="mb-0">{brandDetail.FechaRegistro || 'N/A'}</p>
                            </div>
                            <div className="col-md-6">
                                <strong>Fecha Publicación:</strong>
                                <p className="mb-0">{brandDetail.FechaPublicacion || 'N/A'}</p>
                            </div>
                            <div className="col-md-6">
                                <strong>Fecha Vencimiento:</strong>
                                <p className="mb-0">{brandDetail.FechaVencimiento || 'N/A'}</p>
                            </div>
                            {brandDetail.EtiquetaDescripcion && (
                                <div className="col-12">
                                    <strong>Descripción:</strong>
                                    <p className="mb-0 text-muted">{brandDetail.EtiquetaDescripcion}</p>
                                </div>
                            )}
                            {brandDetail.Proteccion && (
                                <div className="col-12">
                                    <div className="alert alert-info mb-0">
                                        <i className="fa fa-shield-alt me-2"></i>
                                        <strong>Protección:</strong> {brandDetail.Proteccion}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Titulares */}
                {brandDetail.Titulares && brandDetail.Titulares.length > 0 && (
                    <div className="card mb-3">
                        <div className="card-header bg-light">
                            <h6 className="mb-0">
                                <i className="fa fa-user me-2 text-primary"></i>
                                Titular(es)
                            </h6>
                        </div>
                        <div className="card-body">
                            {brandDetail.Titulares.map((titular, index) => (
                                <div key={index} className={index > 0 ? 'border-top pt-3 mt-3' : ''}>
                                    <p className="mb-1"><strong>{titular.Nombre || 'N/A'}</strong></p>
                                    {titular.Domicilio && <p className="mb-1 text-muted small">{titular.Domicilio}</p>}
                                    {titular.NomPais && <p className="mb-0 text-muted small">{titular.NomPais}</p>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Clases */}
                {brandDetail.Clases && brandDetail.Clases.length > 0 && (
                    <div className="card mb-3">
                        <div className="card-header bg-light">
                            <h6 className="mb-0">
                                <i className="fa fa-list me-2 text-primary"></i>
                                Clases de Niza
                            </h6>
                        </div>
                        <div className="card-body p-0">
                            <Table responsive hover className="mb-0">
                                <thead>
                                    <tr>
                                        <th style={{ width: '100px' }}>Clase</th>
                                        <th>Cobertura</th>
                                        <th style={{ width: '150px' }}>Estado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {brandDetail.Clases.map((clase, index) => (
                                        <tr key={index}>
                                            <td>
                                                <Badge bg="secondary">{clase.Id || 'N/A'}</Badge>
                                            </td>
                                            <td className="small">{clase.Cobertura || 'N/A'}</td>
                                            <td className="small">{clase.Instancia || 'N/A'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                )}

                {/* Historial de Instancias */}
                {brandDetail.Instancias && brandDetail.Instancias.length > 0 && (
                    <Accordion className="mb-3">
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>
                                <i className="fa fa-history me-2 text-primary"></i>
                                <strong>Historial de Trámites ({brandDetail.Instancias.length})</strong>
                            </Accordion.Header>
                            <Accordion.Body className="p-0">
                                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    <Table responsive hover className="mb-0">
                                        <thead className="table-light sticky-top">
                                            <tr>
                                                <th style={{ width: '120px' }}>Fecha</th>
                                                <th>Estado</th>
                                                <th>Observación</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {brandDetail.Instancias.map((instancia, index) => (
                                                <tr key={index}>
                                                    <td className="small">{instancia.Fecha || 'N/A'}</td>
                                                    <td className="small">{instancia.EstadoDescripcion || 'N/A'}</td>
                                                    <td className="small text-muted">{instancia.Observacion || '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                )}
            </Modal.Body>
            
            <Modal.Footer>
                <ButtonGeneral
                    type="button"
                    variant="secondary"
                    size="medium"
                    title="Cerrar"
                    onClick={onHide}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default BrandDetailModal;

